<script>
  import { createEventDispatcher } from "svelte";
  import Modal from "../HTMLElements/Modal.svelte";
  import wrappedFetch from "../../wrappedfetch.js";

  export let username = "";
  export let fromPage = "";          // "password", "username", etc.
  export let showModal = false;
  export let declineLabel = "Cancel";
  export let closeOnDecline = false; // true = just close; false = emit "decline"

  const dispatch = createEventDispatcher();

  let preview = null;
  let loading = false;
  let sendingCode = false;
  let error = "";

  export let introText = "We've found an inactive account that may belong to you.";

  // Where to send parents who recognise the account but can no longer
  // access the contact details on it. The Service Desk can update the
  // email on the account (after data-protection checks) so recovery can
  // then complete normally.
  export let supportUrl = "https://www.eadmissions.org.uk/support/";
  let showNoAccessInfo = false;

  // Fetch the masked preview whenever the modal is opened with a username.
  $: if (showModal && username && !preview && !loading) {
    loadPreview();
  }

  async function loadPreview() {
    loading = true;
    error = "";
    try {
      const resp = await wrappedFetch(
        "user/GetRecoveryPreview?username=" + encodeURIComponent(username)
      );
      if (!resp.ok) throw new Error("preview status " + resp.status);
      preview = await resp.json();
    } catch (e) {
      console.error("preview failed", e);
      // Do NOT auto-proceed — if we can't show the masked preview we must not
      // silently move the user forward into the recreate flow.
      error = "We couldn't load your account details. Please try again.";
    } finally {
      loading = false;
    }
  }

  async function confirmItsMe() {
    sendingCode = true;
    error = "";
    try {
      const resp = await wrappedFetch("user/SendRecoveryCode", {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      });
      if (!resp.ok) throw new Error("send code status " + resp.status);
      showModal = false;
      dispatch("proceed");          // caller routes to /account/verifyidentity
    } catch (e) {
      console.error("send code failed", e);
      error = "We couldn't start verification. Please try again.";
    } finally {
      sendingCode = false;
    }
  }

  function decline() {
    showModal = false;
    if (!closeOnDecline) {
      dispatch("decline");
    }
  }
</script>

<Modal bind:showModal>
  <div style="padding: 0.5rem;">
    <h1 class="title is-4">Is this your account?</h1>

   <p class="help" style="margin-bottom: 1rem;">
  {introText}
</p>

    {#if loading}
      <div class="box" style="background:#f5f5f5;">
        <p class="help" style="margin-bottom:0;">Loading your details…</p>
      </div>
    {:else if preview}
      <div class="box" style="background:#f5f5f5;">
        <p class="help" style="margin-bottom:0;">
          <strong>Email:</strong>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{preview.emailMasked}<br>
          <strong>Mobile:</strong>&nbsp;&nbsp;&nbsp;&nbsp;{preview.mobileMasked}<br>
          <strong>Postcode:</strong>&nbsp;{preview.postcodeMasked}
        </p>
      </div>
    {/if}


    {#if error}
      <div class="notification is-invalid">
        <p class="help" style="margin-bottom:0;">{error}</p>
      </div>
    {/if}

    {#if preview}
      <div style="border:1px solid #d9c9de; border-radius:4px; margin-bottom:1rem;">
        <button type="button"
                style="display:flex; width:100%; align-items:center; justify-content:space-between;
                       gap:0.5rem; background:#f5f0f6; color:#6f4077; font-weight:600;
                       font-size:0.85rem; text-align:left; line-height:1.35; border:none;
                       border-radius:4px; padding:0.75em 1em; cursor:pointer;"
                aria-expanded={showNoAccessInfo}
                on:click={() => (showNoAccessInfo = !showNoAccessInfo)}>
          <span>Yes, this is my account, but I no longer have access to these contact details</span>
          <span class="fas {showNoAccessInfo ? 'fa-chevron-up' : 'fa-chevron-down'}" aria-hidden="true" />
        </button>

        {#if showNoAccessInfo}
          <div style="padding:0.75em 1em; border-top:1px solid #d9c9de;">
            <p class="help" style="margin-bottom:0.5rem;">
              If you can no longer access the email address shown above, our support team
              can update it for you so that you can recover your account.
            </p>
            <p class="help" style="margin-bottom:0;">
              Please <a href={supportUrl} target="_blank" style="text-decoration:underline;">contact
              our support team</a> to raise a ticket, stating that you need the email address
              on an archived account updated. To protect your account, the Service Desk will
              ask you some questions to confirm your identity before making any change.
            </p>
          </div>
        {/if}
      </div>
    {/if}

    <div style="display:flex; justify-content:flex-end; gap:0.5rem;">
      <button class="button" on:click={decline} disabled={sendingCode}>
        {declineLabel}
      </button>
      <button class="button is-purple" on:click={confirmItsMe}
              disabled={sendingCode || loading || !preview}>
        {sendingCode ? "Sending code…" : "Yes, that's me"}
      </button>
    </div>
  </div>
</Modal>